import { useCallback, useMemo, useState } from "react"

import {
  deselectAll,
  emptySelection,
  isAllPageRowsSelected,
  isRowSelected,
  selectAll,
  selectRows,
  toggleRowSelection,
} from "src/components/DataTable/selection"

export const useSelection = ({
  selection: controlledSelection,
  onSelectionChange,
  totalCount,
  data,
  pageSelectableIds,
  allSelectableIds,
}) => {
  const [internalSelection, setInternalSelection] = useState(emptySelection())

  const isControlled = controlledSelection !== undefined

  const selection = useMemo(() => {
    const rawSelection = isControlled ? controlledSelection : internalSelection
    return rawSelection || []
  }, [isControlled, controlledSelection, internalSelection])

  const handleSelectionChange = useCallback(
    (newSelection) => {
      if (isControlled && onSelectionChange) {
        onSelectionChange(newSelection)
      } else {
        setInternalSelection(newSelection)
      }
    },
    [isControlled, onSelectionChange]
  )

  const pageIds = useMemo(() => data?.map((row) => row.id) ?? [], [data])

  // Use pageSelectableIds if provided, otherwise all pageIds are selectable
  const effectiveSelectableIds = pageSelectableIds ?? pageIds

  const effectiveTotalCount = isControlled ? totalCount : pageIds.length

  const selectedCount = selection.length

  // Check if all selectable rows on current page are selected
  const allPageRowsSelected = isAllPageRowsSelected(
    selection,
    effectiveSelectableIds
  )

  const handleToggleRow = useCallback(
    (rowId) => {
      const rawSelection = isControlled
        ? controlledSelection
        : internalSelection
      handleSelectionChange(toggleRowSelection(rawSelection ?? [], rowId))
    },
    [
      isControlled,
      controlledSelection,
      internalSelection,
      handleSelectionChange,
    ]
  )

  const handleSelectAll = useCallback(() => {
    const rawSelection = isControlled ? controlledSelection : internalSelection
    handleSelectionChange(
      selectRows(rawSelection ?? [], effectiveSelectableIds)
    )
  }, [
    isControlled,
    controlledSelection,
    internalSelection,
    effectiveSelectableIds,
    handleSelectionChange,
  ])

  const handleDeselectAll = useCallback(() => {
    handleSelectionChange(deselectAll())
  }, [handleSelectionChange])

  const handleSelectAllAcrossPages = useCallback(() => {
    if (allSelectableIds && allSelectableIds.length > 0) {
      handleSelectionChange(selectAll(allSelectableIds))
    }
  }, [allSelectableIds, handleSelectionChange])

  const isSelected = useCallback(
    (rowId) => isRowSelected(selection, rowId),
    [selection]
  )

  return {
    selection,
    selectedCount,
    allPageRowsSelected,
    isSelected,
    toggleRow: handleToggleRow,
    selectAll: handleSelectAll,
    deselectAll: handleDeselectAll,
    selectAllAcrossPages: handleSelectAllAcrossPages,
    isControlled,
    effectiveTotalCount,
  }
}
